import { useState, useCallback, MouseEvent, Suspense, useEffect } from 'react';
import clsx from 'clsx';
import { Cooperative, Portal, MenuItem, NotificationState } from 'models';
import { RouteProps } from 'configs/routes';
import { getRouteByPortal } from 'utils';

import { UserSettingsPicker, Logout } from 'containers';
import { Switch, Route, Link, Redirect, useLocation } from 'react-router-dom';
import { PivotLayout } from 'layouts';
import { Menu as MuiMenu } from '@material-ui/core';
import {
  Backdrop,
  TopBar,
  Logo,
  Box,
  CooperativePicker,
  Navigation,
  Wrapper,
  PageWrapper,
  PortalPicker,
  CustomPageWrapper,
} from 'components';
import { Hidden, Button } from '@material-ui/core';
import MenuIcon from '@material-ui/icons/Menu';
import topBarLogo from 'assets/img/logo_kin.png';

import { useStyles } from './style';

interface Props {
  mainRoutes: RouteProps[];
  notifications: NotificationState;
  loading: boolean;
  menuConfig: MenuItem[];
  cooperatives: Cooperative[];
  currentCooperative: Cooperative | undefined;
  currentPortal: Portal | null;
  portals: Portal[];
  handleChangePortal: (portal: Portal) => void;
  handleChangeCurrentCooperative: (id: string) => void;
}

const Password = () => {
  useEffect(() => {
    window.location.href = '/password';
  }, []);

  return null;
};

const HomeDetails: React.FC<Props> = ({
  loading,
  notifications,
  mainRoutes,
  portals,
  cooperatives,
  currentCooperative,
  menuConfig,
  currentPortal,
  handleChangePortal,
  handleChangeCurrentCooperative,
}) => {
  const classes = useStyles();
  const location = useLocation();
  const [openMenu, setOpenMenu] = useState(false);

  const handleCloseMenu = useCallback(() => {
    setOpenMenu(false);
  }, []);

  const handleMenuClick = useCallback((e: MouseEvent<HTMLElement>) => {
    setOpenMenu(true);
  }, []);

  if (!currentPortal) {
    return null;
  }

  const showServiceDetails =
    (/\/services\/\w+/.test(location.pathname) &&
      currentPortal?.Name === 'Default') ||
    (/\/maintenance\/booking-services\/\w+/.test(location.pathname) &&
      currentPortal?.Name === 'Maintenance');
  const isVisible = menuConfig && currentCooperative && cooperatives.length;
  const isMeterReadingPage = /meter-reading/.test(location.pathname);

  const CooperativePickerComponent = (
    <CooperativePicker
      cooperatives={cooperatives}
      currentCooperative={currentCooperative}
      handleChangeCurrentCooperative={handleChangeCurrentCooperative}
    />
  );

  const LogoComponent = (
    <Link to={getRouteByPortal(currentPortal)}>
      <Logo src={topBarLogo} alt="Logo" />
    </Link>
  );

  const PageContainer = showServiceDetails ? CustomPageWrapper : PageWrapper;

  return (
    <PivotLayout>
      {isVisible ? (
        <>
          <div className={classes.topBarWrapper}>
            <TopBar>
              <Hidden smDown>{LogoComponent}</Hidden>
              <Hidden mdUp>
                <div className={classes.maxHeight}>
                  <Button
                    classes={{ root: classes.menuBtn }}
                    onClick={handleMenuClick}
                  >
                    <MenuIcon className={classes.menuIcon} />
                  </Button>
                  <Hidden xsDown>{LogoComponent}</Hidden>
                </div>
                <MuiMenu
                  open={openMenu}
                  keepMounted={false}
                  onClose={handleCloseMenu}
                  anchorReference="anchorPosition"
                  marginThreshold={0}
                  anchorPosition={{
                    left: 0,
                    top: 70,
                  }}
                  classes={{
                    list: classes.mobileMenuList,
                    paper: classes.mobileMenuPaper,
                  }}
                >
                  <Navigation
                    portal={currentPortal}
                    menu={menuConfig}
                    handleCloseMenu={handleCloseMenu}
                    notifications={notifications}
                  />
                </MuiMenu>
              </Hidden>
              <Hidden smUp>{CooperativePickerComponent}</Hidden>
              <Box>
                <Hidden xsDown>{CooperativePickerComponent}</Hidden>
                {portals.length > 1 ? (
                  <PortalPicker
                    portals={portals}
                    selectedPortal={currentPortal}
                    handleChangePortal={handleChangePortal}
                  />
                ) : null}
                <UserSettingsPicker />
              </Box>
            </TopBar>
          </div>
          <Wrapper>
            <Hidden smDown>
              {showServiceDetails ? null : (
                <Navigation
                  portal={currentPortal}
                  menu={menuConfig}
                  notifications={notifications}
                />
              )}
            </Hidden>
            <PageContainer
              className={clsx({
                [classes.meterReadingPage]: isMeterReadingPage,
              })}
            >
              {menuConfig.length && !loading ? (
                <Suspense fallback={null}>
                  <Switch>
                    {mainRoutes.map((route) => (
                      <Route key={route.path} {...route} />
                    ))}
                    <Route path="/password" component={Password} />
                    <Route path="/logout" component={Logout} />
                    <Redirect to={'/404'} />
                  </Switch>
                </Suspense>
              ) : null}
            </PageContainer>
          </Wrapper>
        </>
      ) : null}
      <Backdrop open={loading} />
    </PivotLayout>
  );
};

export default HomeDetails;
